import { parseDspLine, initialDspState, type DspParserState } from './dsp-parser';

/**
 * Async generator that drives the full DSP streaming lifecycle.
 * Owns: fetch, response validation, ReadableStream reading, line splitting, buffer management, DSP parsing.
 * Yields: DspParserState after processing all complete lines in each network chunk.
 *
 * Per D-04: yields once per reader.read() chunk (natural batching for React re-renders).
 * Per D-05: throws for fatal errors (HTTP errors, network failures). Parse errors carried in state.error.
 * Per D-06: AbortSignal forwarded to fetch(); AbortError propagates naturally through reader.read().
 */
export async function* streamDsp(url: string, signal: AbortSignal): AsyncGenerator<DspParserState> {
  // Forward the user's bearer token via Authorization header so it never
  // appears in the URL query (which leaks into access logs / CloudWatch).

  const response = await fetch(url, {
    method: 'GET',
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error('Failed to connect to streaming service');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let state = initialDspState();

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    let changed = false;
    for (const line of lines) {
      if (line.trim()) {
        state = parseDspLine(state, line);
        changed = true;
      }
    }

    if (changed) {
      yield state;
    }
  }

  // Flush remaining buffer content (per D-04 buffer flush)
  if (buffer.trim()) {
    state = parseDspLine(state, buffer);
    yield state;
  }
}
